import { useDevCycleClient } from '@devcycle/react-client-sdk';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { trackUser } from '@/analytics';
import { getConfig } from '@/constants/config';
import { syncDevCycleFlagsToPostHog } from '@/integrations/devcyclePosthogSync';
import { syncPostHogPersonProperties } from '@/integrations/posthog/personProperties';
import { useAuthTokenStore } from '@/store/authTokenStore';
import { reportException } from '@/utils/error';

const useIdentifyUser = () => {
  const devCycleClient = useDevCycleClient();
  const queryClient = useQueryClient();

  return useCallback(
    async (id: string) => {
      try {
        trackUser(id);
        await devCycleClient.identifyUser({
          user_id: id,
          // ISO 3166-1 alpha-2 for flag targeting (our UK market uses GB).
          country: getConfig().isoCountryCode,
        });
        // Values may be unchanged by identify (no variableUpdated), so sync explicitly to write person properties.
        syncDevCycleFlagsToPostHog(devCycleClient);
        // BankID completion identifies before a token exists; those fetches 401 and the axios handler toasts.
        if (useAuthTokenStore.getState().authToken) {
          void syncPostHogPersonProperties(queryClient, id).catch(error => {
            reportException(error, { level: 'warning' });
          });
        }
      } catch (error) {
        reportException(error, { level: 'warning' });
      }
    },
    [devCycleClient, queryClient],
  );
};

export default useIdentifyUser;
